import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'graves-collective-cart-v1';

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const loadInitialCart = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Basic validation — each item must have product + quantity
    return parsed.filter(
      (i) => i && i.product && typeof i.product.id === 'string' && typeof i.quantity === 'number'
    );
  } catch (err) {
    console.warn('Cart storage corrupted, resetting.', err);
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadInitialCart);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Persist to localStorage whenever cart changes
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn('Failed to persist cart.', err);
    }
  }, [items]);

  const addItem = useCallback((product, selectedSize = null, selectedImage = null, dedication = null, note = null, unitPrice = null) => {
    setItems(prevItems => {
      const hashPart = (s) => btoa(unescape(encodeURIComponent(s))).slice(0, 12);
      const dedicationKey = dedication ? `-ded-${hashPart(dedication)}` : '';
      const noteKey = note ? `-note-${hashPart(note)}` : '';
      const sizeKey = selectedSize ? `-${selectedSize}` : '';
      const itemKey = `${product.id}${sizeKey}${dedicationKey}${noteKey}`;

      const existingIndex = prevItems.findIndex(item => item.itemKey === itemKey);

      if (existingIndex > -1) {
        const newItems = [...prevItems];
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          quantity: newItems[existingIndex].quantity + 1
        };
        return newItems;
      } else {
        return [...prevItems, {
          itemKey,
          product,
          selectedSize,
          selectedImage,
          dedication: dedication || null,
          note: note || null,
          unitPrice: unitPrice ?? product.price,
          quantity: 1,
          addedAt: Date.now()
        }];
      }
    });
  }, []);

  const removeItem = useCallback((itemKey) => {
    setItems(prevItems => prevItems.filter(item => item.itemKey !== itemKey));
  }, []);

  const updateQuantity = useCallback((itemKey, quantity) => {
    if (quantity <= 0) {
      removeItem(itemKey);
      return;
    }
    setItems(prevItems =>
      prevItems.map(item =>
        item.itemKey === itemKey
          ? { ...item, quantity }
          : item
      )
    );
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);
  const toggleCart = useCallback(() => setIsCartOpen(prev => !prev), []);

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const subtotal = items.reduce((sum, item) => {
    const price = item.unitPrice ?? item.product.price ?? 0;
    return sum + (price * item.quantity);
  }, 0);

  const hasInquiryItems = items.some(item => item.product.price === null);

  const value = {
    items,
    itemCount,
    subtotal,
    hasInquiryItems,
    isCartOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    openCart,
    closeCart,
    toggleCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
